import {
  LoyaltyOfferGraphic,
  LoyaltyOfferGraphicEntityType,
} from "../loyaltyTypes";
import { OfferSelectionGroup } from "./offerSelection";

/*
 * The rings a BOGO or bundle collage draws: one per item the offer is made of,
 * not one per item it could be made of. A Buy 1 Get 1 shows two, a bundle of
 * four shows four, however long either option list is.
 *
 * The service's `graphics` lists every option, so it stays the palette and
 * this picks from it: before the offer is redeemed, the first items each group
 * could take; after, the items the guest actually took. Icon and text graphics
 * are part of how the offer reads, not items, and are kept as sent.
 */

const ring = (entityId: string): LoyaltyOfferGraphic => ({
  type: "image",
  entityId,
  entityType: LoyaltyOfferGraphicEntityType.ITEM,
});

const nonItemGraphics = (graphics: LoyaltyOfferGraphic[]) =>
  graphics.filter((graphic) => graphic.type !== "image");

/**
 * The items a group would show before anything is chosen: whatever the offer
 * forces first, then its options in order, round again while it is short and
 * an option can take more — so a bundle of four from two drinks still shows
 * four. Options without a photograph are passed over, since a ring for one
 * would never draw.
 */
function previewEntityIds(
  group: OfferSelectionGroup,
  hasImage: (entityId: string) => boolean
): string[] {
  const options = group.entities.filter((entity) => hasImage(entity.entityId));
  const counts = new Map<string, number>();
  let total = 0;

  for (const entity of options) {
    const forced = Math.min(entity.minQuantity, group.requiredCount - total);
    if (forced > 0) {
      counts.set(entity.entityId, forced);
      total += forced;
    }
  }

  let added = true;
  while (total < group.requiredCount && added) {
    added = false;
    for (const entity of options) {
      const count = counts.get(entity.entityId) ?? 0;
      if (total < group.requiredCount && count < entity.maxQuantity) {
        counts.set(entity.entityId, count + 1);
        total += 1;
        added = true;
      }
    }
  }

  return options.flatMap((entity) =>
    Array<string>(counts.get(entity.entityId) ?? 0).fill(entity.entityId)
  );
}

/** Before redemption: as many rings as the offer has items, group by group. */
export function previewCollageGraphics(
  groups: OfferSelectionGroup[],
  graphics: LoyaltyOfferGraphic[],
  hasImage: (entityId: string) => boolean
): LoyaltyOfferGraphic[] {
  const rings = groups.flatMap((group) => previewEntityIds(group, hasImage));

  return rings.length
    ? [...rings.map(ring), ...nonItemGraphics(graphics)]
    : graphics;
}

/** After redemption: one ring per unit the guest took, from its cart lines. */
export function appliedCollageGraphics(
  unitEntityIds: string[],
  graphics: LoyaltyOfferGraphic[]
): LoyaltyOfferGraphic[] {
  return [...unitEntityIds.map(ring), ...nonItemGraphics(graphics)];
}
